import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Community v1 hooks — votes, comments, public submitted-deals feed.
 *
 * useVote(coupon, onChange) handles optimistic toggling: clicking the
 * heart immediately flips the UI state, then reverts on backend error.
 * onChange is called with {vote_count, user_has_voted} after success
 * so caller can sync external state (e.g. the parent CouponCard).
 */

export function useVote(coupon, onChange) {
  const [voted, setVoted]   = useState(!!coupon?.user_has_voted);
  const [count, setCount]   = useState(coupon?.vote_count ?? 0);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');

  // Keep state in sync if the parent re-renders with a new coupon snapshot
  // (e.g. after pagination or filter change).
  useEffect(() => {
    setVoted(!!coupon?.user_has_voted);
    setCount(coupon?.vote_count ?? 0);
  }, [coupon?.id, coupon?.user_has_voted, coupon?.vote_count]);

  const toggle = useCallback(async () => {
    if (!coupon?.id || busy) return;
    const wasVoted = voted;
    // Optimistic flip — backend confirms shortly.
    setVoted(!wasVoted);
    setCount(c => wasVoted ? Math.max(0, c - 1) : c + 1);
    setBusy(true);
    setError('');
    try {
      const url = `/coupons/${coupon.id}/vote`;
      const { data } = wasVoted ? await api.delete(url) : await api.post(url);
      const fresh = data?.data;
      if (fresh) {
        setVoted(!!fresh.user_has_voted);
        setCount(fresh.vote_count ?? 0);
        onChange?.(fresh);
      }
    } catch (err) {
      // Revert optimistic update on failure.
      setVoted(wasVoted);
      setCount(c => wasVoted ? c + 1 : Math.max(0, c - 1));
      if (err.response?.status === 401) setError('Sign in to vote');
      else setError(err.response?.data?.error || 'Vote failed');
    } finally { setBusy(false); }
  }, [coupon?.id, voted, busy, onChange]);

  return { voted, count, toggle, busy, error };
}

export function useComments(couponId) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const refresh = useCallback(async () => {
    if (!couponId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/coupons/${couponId}/comments`);
      setComments(data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load comments');
    } finally { setLoading(false); }
  }, [couponId]);

  useEffect(() => { refresh(); }, [refresh]);

  const post = useCallback(async (body) => {
    const { data } = await api.post(`/coupons/${couponId}/comments`, { body });
    const newComment = data?.data;
    if (newComment) setComments(prev => [newComment, ...prev]);
    return newComment;
  }, [couponId]);

  const remove = useCallback(async (commentId) => {
    await api.delete(`/comments/${commentId}`);
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, body: '[deleted by user]', is_deleted: true } : c));
  }, []);

  const edit = useCallback(async (commentId, body) => {
    const { data } = await api.put(`/comments/${commentId}`, { body });
    const updated = data?.data;
    if (updated) {
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, body: updated.body, edited_at: updated.edited_at } : c));
    }
    return updated;
  }, []);

  return { comments, loading, error, post, remove, edit, refresh };
}

export function useCommunityFeed(sort = 'top', limit = 24) {
  const [deals, setDeals]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/community/submitted-deals', { params: { sort, limit } });
      setDeals(data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load community feed');
    } finally { setLoading(false); }
  }, [sort, limit]);

  useEffect(() => { refresh(); }, [refresh]);

  return { deals, loading, error, refresh };
}
